/**
 * ReadStatusBadge — Spec 2026-08-02 point #6 + point #3 (rev).
 *
 * Badge pill affichant l'état de lecture d'une tâche pour ses exécuteurs.
 * Deux modes :
 *   - Compact (par défaut, colonne du tableau) : "Pas vu" · "Vu · [délai]"
 *     si 1 seul exécuteur, sinon "N/M vus" (couleur graduelle).
 *   - Detail (fiche tâche) : décomposé par exécuteur avec délais individuels.
 *
 * Spec 2026-08-02 point #3 rev — pour les tâches multi-exécuteurs, un
 * popover Radix HoverCard affiche la liste NOMINATIVE de chaque exécuteur
 * avec son statut individuel au survol (au lieu d'un tooltip générique).
 *
 * S'appuie sur les champs `first_read_at` et `reaction_delay_seconds`
 * stockés côté backend au 1er ouverture de la fiche par l'exécuteur
 * (POST /api/tasks/{id}/mark-read). N'affecte AUCUN cycle de statut.
 */
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { businessSecondsBetween } from "@/lib/businessHours";
import UserAvatar from "@/components/ui/UserAvatar";
import { useUserDirectory } from "@/lib/userDirectory";

/** Formatage relatif court : <60s "à l'instant" · <60min "N min" · <24h "N h" · ≥24h "N j". */
export function formatReactionDelay(seconds) {
  if (seconds == null || seconds < 0) return "";
  if (seconds < 60) return "à l'instant";
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const days = Math.round(h / 24);
  return `${days} j`;
}

/** Extrait la liste des assignments — spec 2026-02-XX rev5 : tous rôles
 *  (executor + helper + manager) sont désormais trackés pour la lecture. */
function executorAssignments(task) {
  return task?.assignments || [];
}

/**
 * Spec 2026-02-XX (Feb 2026) — délai « pas vu » depuis la 1ère assignation.
 * Ancre : min(all assigned_at, task.created_at) — la ``created_at`` est
 * TOUJOURS incluse pour éviter le bug rev5 : si un helper récent (ex: Fawzi
 * ajouté après coup) a un ``assigned_at`` frais alors que l'exécuteur
 * d'origine n'a pas d'``assigned_at`` (tâche pré-tracking 2026-08-01), le
 * simple min sur les assignments donnait une ancre trop récente et le
 * délai apparaissait comme < 1h → pas d'escalade orange, pas de durée
 * affichée. En incluant ``created_at`` on garantit qu'on ne perd jamais
 * l'ancienneté réelle de la tâche.
 * Retourne les secondes OUVRÉES écoulées (Dim-Jeu 8-17h Alger) ou null.
 */
function unreadElapsedSeconds(task) {
  const ass = executorAssignments(task);
  if (!ass.length) return null;
  const timestamps = ass
    .map((a) => a.assigned_at)
    .filter(Boolean)
    .map((iso) => new Date(iso).getTime())
    .filter((t) => !Number.isNaN(t));
  if (task?.created_at) {
    const c = new Date(task.created_at).getTime();
    if (!Number.isNaN(c)) timestamps.push(c);
  }
  if (!timestamps.length) return null;
  const anchorMs = Math.min(...timestamps);
  return businessSecondsBetween(anchorMs, Date.now());
}

/** Seuil d'escalade en orange : 1 heure sans lecture. */
const UNREAD_OVERDUE_SECONDS = 3600;

/** Renvoie {readCount, totalExecs, firstDelaySeconds} agrégé. */
function computeAggregate(task) {
  const execs = executorAssignments(task);
  const read = execs.filter((a) => a.first_read_at);
  const totalExecs = execs.length;
  const readCount = read.length;
  // Délai du plus rapide lecteur (pour affichage "Vu · N min" en solo).
  const firstDelaySeconds = read.length
    ? Math.min(
        ...read
          .map((a) => a.reaction_delay_seconds)
          .filter((v) => v != null && v >= 0),
      )
    : null;
  return { readCount, totalExecs, firstDelaySeconds };
}

const STYLES = {
  // Spec rev65 (2026-08-02) — retour au style translucide plus doux.
  //   • Pas vu (unread/unreadOverdue)   → gris neutre (#6B7280) sur fond
  //     sombre #262931 (proche du fond de ligne mais légèrement contrasté).
  //   • Vu (read) et partial            → vert #22C55E sur fond
  //     rgba(34,197,94,0.14) — volontairement DISTINCT du vert WhatsApp
  //     de la colonne Discussion (#25D366) pour éviter la confusion.
  //   • none (aucun exécuteur)          → cadre dashed gris inchangé.
  unread: {
    bg: "#262931",
    border: "1px solid rgba(107,114,128,0.30)",
    color: "#6B7280",
  },
  unreadOverdue: {
    bg: "#262931",
    border: "1px solid rgba(107,114,128,0.30)",
    color: "#6B7280",
  },
  partial: {
    bg: "rgba(34,197,94,0.14)",
    border: "1px solid rgba(34,197,94,0.35)",
    color: "#22C55E",
  },
  read: {
    bg: "rgba(34,197,94,0.14)",
    border: "1px solid rgba(34,197,94,0.35)",
    color: "#22C55E",
  },
  none: {
    bg: "transparent",
    border: "1px dashed rgba(255,255,255,0.10)",
    color: "#6B7280",
  },
};

function Pill({ style, icon: Icon, label, testId, title }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap tabular-nums"
      style={{
        background: style.bg,
        border: style.border,
        color: style.color,
      }}
      data-testid={testId}
      title={title || undefined}
    >
      {Icon ? <Icon size={11} strokeWidth={2.2} /> : null}
      {label}
    </span>
  );
}

/**
 * Spec rev136 (2026-02-06) — Refonte du badge Lecture :
 *   • Rendu en « pastilles empilées » (une par assigné, 16px,
 *     chevauchement -4px, bordure 2px couleur de fond).
 *   • Verte avec œil pour « a vu », grise avec œil barré pour « non vu ».
 *   • Tooltip HoverCard « LECTURE PAR ASSIGNÉ » listant, pour chaque
 *     personne : icône œil (vert/gris) + avatar (initiales, mêmes
 *     couleurs qu'ailleurs dans l'app) + date/heure exacte de lecture
 *     à droite (ou « Non vu » en gris si pas encore lu). Le NOM complet
 *     n'est PAS affiché — l'avatar suffit.
 */
export default function ReadStatusBadge({ task, usersById = {}, surfaceBg = "#1A1A1A" }) {
  const testId = `read-badge-${task?.id || "none"}`;
  const execs = executorAssignments(task);

  if (execs.length === 0) {
    return (
      <span
        className="relative inline-flex items-center justify-center rounded-full"
        style={{
          // Spec iter126 pt.3 (Feb 2026) — pill 26px + icône 15px pour
          // matcher la taille des avatars Créé/Assigné(s) voisins.
          width: 26, height: 26,
          background: "transparent",
          border: "1px dashed rgba(255,255,255,0.10)",
          color: "#6B7280",
        }}
        title="Aucun exécuteur assigné"
        data-testid={testId}
      >
        <EyeOff size={15} strokeWidth={2.2} />
      </span>
    );
  }

  // Spec iter126 pt.3 (Feb 2026) — pill agrandi à 26 px (comme les avatars
  // des colonnes Créé/Assigné) + icône œil 15 px pour rester proportionnée.
  const pillSize = 26;
  const overlap = -6;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex items-center cursor-help"
          data-testid={testId}
        >
          {execs.map((a, i) => {
            const seen = !!a.first_read_at;
            const style = seen
              ? { background: "rgba(34,197,94,0.20)", color: "#22C55E", border: `2px solid ${surfaceBg}` }
              : { background: "rgba(107,114,128,0.20)", color: "#9CA3AF", border: `2px solid ${surfaceBg}` };
            const Icon = seen ? Eye : EyeOff;
            return (
              <span
                key={a.id || a.user_id}
                className="rounded-full inline-flex items-center justify-center shrink-0"
                style={{
                  width: pillSize, height: pillSize,
                  marginLeft: i === 0 ? 0 : overlap,
                  zIndex: execs.length - i,
                  ...style,
                }}
                data-testid={`${testId}-pill-${a.user_id}`}
              >
                <Icon size={15} strokeWidth={2.4} />
              </span>
            );
          })}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="p-0 border-0 shadow-xl"
        style={{ background: "transparent" }}
        data-testid={`${testId}-detail`}
      >
        <ReadStatusDetail task={task} usersById={usersById} />
      </HoverCardContent>
    </HoverCard>
  );
}

/** Formate date exacte pour tooltip : « 06 août 26 · 14:32 ». */
function fmtExactDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return null; }
}

/** Spec iter252 (2026-03-01) — pour chaque assigné, retourne 4 valeurs :
 *  - beforeSeconds : temps entre assignation et lecture (secondes ouvrées).
 *  - beforeIsRunning : true tant que non vu (« Avant vu » en cours).
 *  - afterSeconds : temps entre lecture et action prise (completed_at). Si
 *    non complété mais vu, retourne now - first_read_at. Null si pas vu.
 *  - afterIsRunning : true si vu mais pas encore complété (ni tâche fermée).
 *  Le seuil « raisonnable » pour passer en orange sur les deux durées
 *  est le même que pour l'unread (1 heure ouvrée) — spec cohérente.
 */
function computeReadTimings(assignment, task) {
  const now = Date.now();
  const assignedAtIso = assignment.assigned_at || task?.created_at || null;
  const anchorMs = assignedAtIso ? new Date(assignedAtIso).getTime() : NaN;

  // « Avant vu »
  let beforeSeconds = null;
  let beforeIsRunning = false;
  if (assignment.first_read_at) {
    // Priorité au champ figé backend (précis à la seconde).
    beforeSeconds =
      assignment.reaction_delay_seconds != null
        ? assignment.reaction_delay_seconds
        : (!Number.isNaN(anchorMs)
            ? businessSecondsBetween(anchorMs, new Date(assignment.first_read_at).getTime())
            : null);
  } else if (!Number.isNaN(anchorMs)) {
    beforeSeconds = businessSecondsBetween(anchorMs, now);
    beforeIsRunning = true;
  }

  // « Après vu »
  let afterSeconds = null;
  let afterIsRunning = false;
  if (assignment.first_read_at) {
    const readMs = new Date(assignment.first_read_at).getTime();
    if (assignment.completed_at) {
      afterSeconds = businessSecondsBetween(readMs, new Date(assignment.completed_at).getTime());
    } else {
      afterSeconds = businessSecondsBetween(readMs, now);
      afterIsRunning = true;
    }
  }
  return { beforeSeconds, beforeIsRunning, afterSeconds, afterIsRunning };
}

/** Detail popover — spec rev136 (2026-02-06) + iter252 (2026-03-01).
 *
 * Liste par assigné, chaque bloc contient :
 *  1. Ligne existante inchangée : icône œil + avatar + statut « Vu · date »
 *     ou « Non vu ».
 *  2. Section timing en dessous, séparée par un liseré, 2 colonnes :
 *     - « Avant vu »  = délai entre assignation et lecture. Si toujours
 *       non vu → temps écoulé depuis l'assignation, avec « (en cours) »
 *       en orange (seuil : 1h ouvrée).
 *     - « Après vu » = « — » gris tant que non vu. Une fois vu, temps
 *       entre la lecture et l'action prise (`completed_at`). Si pas
 *       encore d'action → « (en cours) » orange après 1h ouvrée.
 *     Une fois `completed_at` fixé, les deux valeurs se figent en
 *     couleur neutre (sans « (en cours) »).
 */
function ReadStatusDetail({ task, usersById }) {
  const { byId: dirById } = useUserDirectory();
  const execs = executorAssignments(task);
  const readCount = execs.filter((a) => a.first_read_at).length;
  const totalExecs = execs.length;
  return (
    <div
      className="rounded-lg p-3 min-w-[300px] max-w-[380px]"
      style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        Lecture par assigné ({readCount}/{totalExecs} vus)
      </div>
      <ul className="space-y-2.5">
        {execs.map((a) => {
          const u = dirById[a.user_id] || usersById[a.user_id];
          const seen = !!a.first_read_at;
          const statusColor = seen ? "#22C55E" : "#EF4444";
          const Icon = seen ? Eye : EyeOff;
          const exact = seen ? fmtExactDateTime(a.first_read_at) : null;
          const t = computeReadTimings(a, task);
          const isOverdue = (s) => s != null && s > UNREAD_OVERDUE_SECONDS;
          // « Avant vu »
          const beforeText = t.beforeSeconds != null ? formatReactionDelay(t.beforeSeconds) : "—";
          const beforeSuffix = t.beforeIsRunning ? " (en cours)" : "";
          const beforeColor = t.beforeIsRunning
            ? (isOverdue(t.beforeSeconds) ? "#F97316" : "#9CA3AF")
            : "#D1D5DB";
          // « Après vu »
          let afterText, afterColor, afterSuffix = "";
          if (!seen) {
            afterText = "—";
            afterColor = "#4B5563";
          } else if (t.afterIsRunning) {
            afterText = t.afterSeconds != null ? formatReactionDelay(t.afterSeconds) : "—";
            afterSuffix = " (en cours)";
            afterColor = isOverdue(t.afterSeconds) ? "#F97316" : "#9CA3AF";
          } else {
            afterText = t.afterSeconds != null ? formatReactionDelay(t.afterSeconds) : "—";
            afterColor = "#D1D5DB";
          }
          return (
            <li
              key={a.id || a.user_id}
              className="text-[11px]"
              data-testid={`read-detail-${task.id}-${a.user_id}`}
            >
              {/* Ligne existante inchangée — icône + avatar + statut. */}
              <div className="flex items-center gap-2.5">
                <Icon size={12} strokeWidth={2.4} style={{ color: statusColor }} className="shrink-0" />
                <UserAvatar user={u} size={20} showPopover={false} className="shrink-0" />
                {u && (
                  <span className="text-[11px] text-gray-200 truncate">
                    {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—"}
                  </span>
                )}
                <span className="flex-1" />
                <span
                  style={{ color: statusColor }}
                  className="whitespace-nowrap tabular-nums text-[10.5px] font-semibold"
                >
                  {seen ? (exact || "Vu") : "Non vu"}
                </span>
              </div>
              {/* Section timing — spec iter252. */}
              <div
                className="mt-1.5 pt-1.5 grid grid-cols-2 gap-2"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`read-timing-${task.id}-${a.user_id}`}
              >
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                    Avant vu
                  </div>
                  <div
                    className="text-[11px] tabular-nums mt-0.5"
                    style={{ color: beforeColor }}
                    data-testid={`read-before-${task.id}-${a.user_id}`}
                  >
                    {beforeText}
                    {beforeSuffix && (
                      <span className="text-[9.5px] opacity-80 ml-1">{beforeSuffix.trim()}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                    Après vu
                  </div>
                  <div
                    className="text-[11px] tabular-nums mt-0.5"
                    style={{ color: afterColor }}
                    data-testid={`read-after-${task.id}-${a.user_id}`}
                  >
                    {afterText}
                    {afterSuffix && (
                      <span className="text-[9.5px] opacity-80 ml-1">{afterSuffix.trim()}</span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
